import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/authContext';
import { Layout } from '../../components/Layout';
import { assignInstaller, completeProject, deletePhoto, getProject, getUploadUrl, listPhotos, recordPhoto, unassignInstaller, updateProject, } from './api';
import { listInstallers } from './api';
function statusBadgeStyle(status) {
    switch (status) {
        case 'draft':
            return styles.badgeGray;
        case 'assigned':
            return styles.badgeBlue;
        case 'in_progress':
            return styles.badgeOrange;
        case 'completed':
            return styles.badgeGreen;
        default:
            return styles.badgeGray;
    }
}
function statusLabel(status) {
    switch (status) {
        case 'draft':
            return 'Draft';
        case 'assigned':
            return 'Assigned';
        case 'in_progress':
            return 'In Progress';
        case 'completed':
            return 'Completed';
        default:
            return status;
    }
}
export const ProjectDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [draft, setDraft] = useState({});
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [installers, setInstallers] = useState([]);
    const [selectedInstallerId, setSelectedInstallerId] = useState('');
    const [assignLoading, setAssignLoading] = useState(false);
    const [completionNotes, setCompletionNotes] = useState('');
    const [completing, setCompleting] = useState(false);
    const [photoLoading, setPhotoLoading] = useState(false);
    const [photosList, setPhotosList] = useState([]);
    const [lightboxPhoto, setLightboxPhoto] = useState(null);
    const beforeFileRef = useRef(null);
    const afterFileRef = useRef(null);
    const loadPhotoUrls = useCallback(async () => {
        if (!id)
            return;
        try {
            const photos = await listPhotos(id);
            setPhotosList(photos);
        }
        catch {
            // non-critical — silently ignore
        }
    }, [id]);
    const loadProject = useCallback(async () => {
        if (!id)
            return;
        setLoading(true);
        setError(null);
        try {
            const p = await getProject(id);
            setProject(p);
            // Load presigned URLs separately (getProject doesn't include them)
            const photos = await listPhotos(id);
            setPhotosList(photos);
        }
        catch (e) {
            setError(e.message ?? 'Failed to load project');
        }
        finally {
            setLoading(false);
        }
    }, [id]);
    useEffect(() => {
        loadProject();
    }, [loadProject]);
    useEffect(() => {
        listInstallers({ status: 'active', limit: 100 })
            .then((res) => {
            setInstallers(res.items);
            if (res.items.length > 0)
                setSelectedInstallerId(res.items[0].id);
        })
            .catch(() => { });
    }, []);
    const startEdit = () => {
        if (!project)
            return;
        setDraft({ ...project });
        setSaveError(null);
        setEditMode(true);
    };
    const cancelEdit = () => {
        setEditMode(false);
        setDraft({});
        setSaveError(null);
    };
    const handleSave = async () => {
        if (!id || !project)
            return;
        setSaving(true);
        setSaveError(null);
        try {
            const updated = await updateProject(id, draft);
            setProject(updated);
            setEditMode(false);
            setDraft({});
        }
        catch (e) {
            setSaveError(e.message ?? 'Failed to save');
        }
        finally {
            setSaving(false);
        }
    };
    const handleAssign = async () => {
        if (!id || !selectedInstallerId)
            return;
        const installer = installers.find((i) => i.id === selectedInstallerId);
        if (!installer)
            return;
        setAssignLoading(true);
        try {
            const updated = await assignInstaller(id, installer.id, `${installer.firstName} ${installer.lastName}`);
            setProject(updated);
        }
        catch (e) {
            setError(e.message ?? 'Failed to assign installer');
        }
        finally {
            setAssignLoading(false);
        }
    };
    const handleUnassign = async () => {
        if (!id)
            return;
        setAssignLoading(true);
        try {
            const updated = await unassignInstaller(id);
            setProject(updated);
        }
        catch (e) {
            setError(e.message ?? 'Failed to unassign installer');
        }
        finally {
            setAssignLoading(false);
        }
    };
    const handleComplete = async () => {
        if (!id)
            return;
        setCompleting(true);
        try {
            const updated = await completeProject(id, completionNotes || undefined);
            setProject(updated);
            setCompletionNotes('');
        }
        catch (e) {
            setError(e.message ?? 'Failed to complete project');
        }
        finally {
            setCompleting(false);
        }
    };
    const handlePhotoUpload = async (file, type) => {
        if (!id)
            return;
        setPhotoLoading(true);
        try {
            const { photoId, s3Key, uploadUrl } = await getUploadUrl(id, type, file.name, file.type);
            await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': file.type },
            });
            await recordPhoto(id, photoId, type, s3Key, user?.username ?? 'unknown');
            await loadPhotoUrls();
            const updated = await getProject(id);
            setProject(updated);
        }
        catch (e) {
            setError(e.message ?? 'Failed to upload photo');
        }
        finally {
            setPhotoLoading(false);
        }
    };
    const handleDeletePhoto = async (photoId) => {
        if (!id)
            return;
        try {
            await deletePhoto(id, photoId);
            setPhotosList((prev) => prev.filter((p) => p.photoId !== photoId));
            const updated = await getProject(id);
            setProject(updated);
        }
        catch (e) {
            setError(e.message ?? 'Failed to delete photo');
        }
    };
    if (loading) {
        return (_jsx(Layout, { children: _jsx("div", { style: styles.loadingCenter, children: _jsx("div", { style: styles.spinner }) }) }));
    }
    if (error && !project) {
        return (_jsxs(Layout, { children: [_jsx("div", { style: styles.errorBanner, children: error }), _jsx("button", { style: styles.backLink, onClick: () => navigate('/projects'), children: "\u2190 Back to Projects" })] }));
    }
    if (!project)
        return null;
    const beforePhotos = photosList.filter((p) => p.type === 'before');
    const afterPhotos = photosList.filter((p) => p.type === 'after');
    const setDraftAddr = (field, value) => {
        setDraft((prev) => ({
            ...prev,
            siteAddress: { ...(prev.siteAddress ?? project.siteAddress), [field]: value },
        }));
    };
    return (_jsxs(Layout, { children: [_jsxs("div", { children: [_jsx("button", { style: styles.backLink, onClick: () => navigate('/projects'), children: "\u2190 Projects" }), _jsxs("div", { style: styles.card, children: [_jsxs("div", { style: styles.cardHeaderRow, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }, children: [_jsx("h2", { style: styles.projectTitle, children: editMode ? (draft.title ?? project.title) : project.title }), _jsx("span", { style: statusBadgeStyle(project.status), children: statusLabel(project.status) }), project.priority === 'urgent' && (_jsx("span", { style: styles.badgeOrange, children: "Urgent" }))] }), _jsx("div", { style: { display: 'flex', gap: '10px', flexWrap: 'wrap' }, children: !editMode ? (_jsx("button", { style: styles.secondaryButton, onClick: startEdit, children: "Edit" })) : (_jsxs(_Fragment, { children: [_jsx("button", { style: styles.ghostButton, onClick: cancelEdit, disabled: saving, children: "Cancel" }), _jsx("button", { style: styles.primaryButton, onClick: handleSave, disabled: saving, children: saving ? 'Saving…' : 'Save' })] })) })] }), saveError && _jsx("div", { style: { ...styles.errorBanner, marginTop: '12px' }, children: saveError }), error && _jsx("div", { style: { ...styles.errorBanner, marginTop: '12px' }, children: error })] }), _jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.sectionTitle, children: "Project Info" }), editMode ? (_jsxs("div", { style: styles.fieldGrid, children: [_jsxs("div", { style: styles.fieldGroup, children: [_jsx("label", { style: styles.fieldLabel, children: "Title" }), _jsx("input", { style: styles.input, value: draft.title ?? '', onChange: (e) => setDraft((p) => ({ ...p, title: e.target.value })) })] }), _jsxs("div", { style: { ...styles.fieldGroup, gridColumn: '1 / -1' }, children: [_jsx("label", { style: styles.fieldLabel, children: "Description" }), _jsx("textarea", { style: styles.textarea, rows: 3, value: draft.description ?? '', onChange: (e) => setDraft((p) => ({ ...p, description: e.target.value })) })] }), _jsxs("div", { style: { ...styles.fieldGroup, gridColumn: '1 / -1' }, children: [_jsx("label", { style: styles.fieldLabel, children: "Instructions" }), _jsx("textarea", { style: styles.textarea, rows: 5, value: draft.instructions ?? '', onChange: (e) => setDraft((p) => ({ ...p, instructions: e.target.value })) })] }), _jsxs("div", { style: styles.fieldGroup, children: [_jsx("label", { style: styles.fieldLabel, children: "Scheduled Date" }), _jsx("input", { style: styles.input, type: "date", value: draft.scheduledDate ?? '', onChange: (e) => setDraft((p) => ({ ...p, scheduledDate: e.target.value })) })] }), _jsxs("div", { style: styles.fieldGroup, children: [_jsx("label", { style: styles.fieldLabel, children: "Scheduled Start" }), _jsx("input", { style: styles.input, type: "time", value: draft.scheduledStart ?? '', onChange: (e) => setDraft((p) => ({ ...p, scheduledStart: e.target.value })) })] }), _jsxs("div", { style: styles.fieldGroup, children: [_jsx("label", { style: styles.fieldLabel, children: "Scheduled End" }), _jsx("input", { style: styles.input, type: "time", value: draft.scheduledEnd ?? '', onChange: (e) => setDraft((p) => ({ ...p, scheduledEnd: e.target.value })) })] }), _jsxs("div", { style: styles.fieldGroup, children: [_jsx("label", { style: styles.fieldLabel, children: "Priority" }), _jsxs("select", { style: styles.input, value: draft.priority ?? 'normal', onChange: (e) => setDraft((p) => ({ ...p, priority: e.target.value })), children: [_jsx("option", { value: "normal", children: "Normal" }), _jsx("option", { value: "urgent", children: "Urgent" })] })] }), _jsxs("div", { style: { ...styles.fieldGroup, gridColumn: '1 / -1' }, children: [_jsx("label", { style: styles.fieldLabel, children: "Site Address" }), _jsx("input", { style: { ...styles.input, marginBottom: '8px' }, placeholder: "Street", value: draft.siteAddress?.street ?? '', onChange: (e) => setDraftAddr('street', e.target.value) }), _jsxs("div", { style: { display: 'flex', gap: '8px' }, children: [_jsx("input", { style: styles.input, placeholder: "City", value: draft.siteAddress?.city ?? '', onChange: (e) => setDraftAddr('city', e.target.value) }), _jsx("input", { style: { ...styles.input, maxWidth: '80px' }, placeholder: "State", maxLength: 2, value: draft.siteAddress?.state ?? '', onChange: (e) => setDraftAddr('state', e.target.value) }), _jsx("input", { style: { ...styles.input, maxWidth: '110px' }, placeholder: "ZIP", value: draft.siteAddress?.zip ?? '', onChange: (e) => setDraftAddr('zip', e.target.value) })] })] })] })) : (_jsxs("div", { style: styles.fieldGrid, children: [_jsx(InfoField, { label: "Customer", value: project.customerName }), _jsx(InfoField, { label: "Customer ID", value: project.customerId }), _jsx(InfoField, { label: "Priority", value: project.priority === 'urgent' ? 'Urgent' : 'Normal' }), _jsx(InfoField, { label: "Scheduled Date", value: project.scheduledDate
                                            ? new Date(project.scheduledDate).toLocaleDateString()
                                            : '—' }), _jsx(InfoField, { label: "Scheduled Start", value: project.scheduledStart ?? '—' }), _jsx(InfoField, { label: "Scheduled End", value: project.scheduledEnd ?? '—' }), _jsxs("div", { style: { ...styles.fieldGroup, gridColumn: '1 / -1' }, children: [_jsx("label", { style: styles.fieldLabel, children: "Description" }), _jsx("p", { style: styles.fieldValue, children: project.description || '—' })] }), _jsxs("div", { style: { ...styles.fieldGroup, gridColumn: '1 / -1' }, children: [_jsx("label", { style: styles.fieldLabel, children: "Instructions" }), _jsx("p", { style: { ...styles.fieldValue, whiteSpace: 'pre-wrap' }, children: project.instructions || '—' })] }), _jsxs("div", { style: { ...styles.fieldGroup, gridColumn: '1 / -1' }, children: [_jsx("label", { style: styles.fieldLabel, children: "Site Address" }), _jsxs("p", { style: styles.fieldValue, children: [project.siteAddress.street, _jsx("br", {}), project.siteAddress.city, ", ", project.siteAddress.state, " ", project.siteAddress.zip] })] })] }))] }), _jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.sectionTitle, children: "Assignment" }), project.assignedInstallerId ? (_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }, children: [_jsxs("div", { children: [_jsx("p", { style: styles.fieldValue, children: project.assignedInstallerName }), project.assignedAt && (_jsxs("p", { style: { ...styles.fieldValue, fontSize: '13px', color: '#86868b', margin: '4px 0 0 0' }, children: ["Assigned ", new Date(project.assignedAt).toLocaleString()] }))] }), _jsx("button", { style: styles.dangerButton, onClick: handleUnassign, disabled: assignLoading, children: assignLoading ? 'Unassigning…' : 'Unassign' })] })) : project.status !== 'completed' ? (_jsx("div", { style: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }, children: installers.length === 0 ? (_jsx("p", { style: { color: '#86868b', fontSize: '14px', margin: 0 }, children: "No active installers available." })) : (_jsxs(_Fragment, { children: [_jsx("select", { style: { ...styles.input, maxWidth: '280px' }, value: selectedInstallerId, onChange: (e) => setSelectedInstallerId(e.target.value), children: installers.map((ins) => (_jsxs("option", { value: ins.id, children: [ins.firstName, " ", ins.lastName] }, ins.id))) }), _jsx("button", { style: styles.primaryButton, onClick: handleAssign, disabled: assignLoading || !selectedInstallerId, children: assignLoading ? 'Assigning…' : 'Assign' })] })) })) : (_jsx("p", { style: { color: '#86868b', fontSize: '14px', margin: 0 }, children: "No installer was assigned." }))] }), _jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.sectionTitle, children: "Check-In History" }), project.checkIns.length === 0 ? (_jsx("p", { style: styles.emptySubText, children: "No check-ins yet." })) : (_jsx("div", { children: project.checkIns.map((entry, idx) => (_jsxs("div", { style: styles.checkInRow, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '10px' }, children: [_jsx("span", { style: entry.type === 'checkin' ? styles.badgeGreen : styles.badgeGray, children: entry.type === 'checkin' ? 'Check In' : 'Check Out' }), _jsx("span", { style: { fontSize: '14px', color: '#1d1d1f' }, children: new Date(entry.timestamp).toLocaleString() })] }), entry.type === 'checkin' && (_jsxs("span", { style: { fontSize: '13px', color: '#86868b' }, children: [entry.latitude.toFixed(6), ", ", entry.longitude.toFixed(6)] }))] }, idx))) }))] }), _jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.sectionTitle, children: "Photos" }), photoLoading && (_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }, children: [_jsx("div", { style: styles.spinnerSmall }), _jsx("span", { style: { fontSize: '14px', color: '#86868b' }, children: "Uploading\u2026" })] })), _jsxs("div", { style: { display: 'flex', gap: '24px', flexWrap: 'wrap' }, children: [_jsxs("div", { style: styles.photoColumn, children: [_jsxs("p", { style: styles.photoColumnLabel, children: ["Before Photos (", beforePhotos.length, ")"] }), _jsx("div", { style: styles.photoGrid, children: beforePhotos.map((photo) => (_jsxs("div", { style: styles.photoWrapper, children: [_jsx("img", { src: photo.url, alt: "Before", style: styles.photoImg, onClick: () => setLightboxPhoto(photo), title: "Click to enlarge" }), _jsx("button", { style: styles.photoDeleteBtn, onClick: () => handleDeletePhoto(photo.photoId), title: "Delete photo", children: "\u00D7" })] }, photo.photoId))) }), _jsx("input", { ref: beforeFileRef, type: "file", accept: "image/*", style: { display: 'none' }, onChange: (e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file)
                                                        handlePhotoUpload(file, 'before');
                                                    e.target.value = '';
                                                } }), _jsx("button", { style: styles.uploadButton, onClick: () => beforeFileRef.current?.click(), disabled: photoLoading, children: "+ Upload Before Photo" })] }), _jsxs("div", { style: styles.photoColumn, children: [_jsxs("p", { style: styles.photoColumnLabel, children: ["After Photos (", afterPhotos.length, ")"] }), _jsx("div", { style: styles.photoGrid, children: afterPhotos.map((photo) => (_jsxs("div", { style: styles.photoWrapper, children: [_jsx("img", { src: photo.url, alt: "After", style: styles.photoImg, onClick: () => setLightboxPhoto(photo), title: "Click to enlarge" }), _jsx("button", { style: styles.photoDeleteBtn, onClick: () => handleDeletePhoto(photo.photoId), title: "Delete photo", children: "\u00D7" })] }, photo.photoId))) }), _jsx("input", { ref: afterFileRef, type: "file", accept: "image/*", style: { display: 'none' }, onChange: (e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file)
                                                        handlePhotoUpload(file, 'after');
                                                    e.target.value = '';
                                                } }), _jsx("button", { style: styles.uploadButton, onClick: () => afterFileRef.current?.click(), disabled: photoLoading, children: "+ Upload After Photo" })] })] })] }), (project.status === 'completed' || project.status === 'in_progress') && (_jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.sectionTitle, children: "Completion" }), project.status === 'completed' ? (_jsxs("div", { children: [_jsx(InfoField, { label: "Completed At", value: project.completedAt
                                            ? new Date(project.completedAt).toLocaleString()
                                            : '—' }), project.completionNotes && (_jsxs("div", { style: { ...styles.fieldGroup, marginTop: '16px' }, children: [_jsx("label", { style: styles.fieldLabel, children: "Completion Notes" }), _jsx("p", { style: { ...styles.fieldValue, whiteSpace: 'pre-wrap' }, children: project.completionNotes })] }))] })) : (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '480px' }, children: [_jsx("label", { style: styles.fieldLabel, children: "Completion Notes" }), _jsx("textarea", { style: styles.textarea, rows: 4, placeholder: "Optional notes about the completed work\u2026", value: completionNotes, onChange: (e) => setCompletionNotes(e.target.value) }), _jsx("button", { style: styles.successButton, onClick: handleComplete, disabled: completing, children: completing ? 'Completing…' : 'Mark Complete' })] }))] }))] }), lightboxPhoto && (_jsx("div", { style: styles.lightboxOverlay, onClick: () => setLightboxPhoto(null), children: _jsxs("div", { style: styles.lightboxContent, onClick: (e) => e.stopPropagation(), children: [_jsx("button", { style: styles.lightboxClose, onClick: () => setLightboxPhoto(null), children: "\u00D7" }), _jsxs("div", { style: styles.lightboxLabel, children: [lightboxPhoto.type === 'before' ? 'Before' : 'After', " \u00B7", ' ', new Date(lightboxPhoto.uploadedAt).toLocaleString()] }), _jsx("img", { src: lightboxPhoto.url, alt: lightboxPhoto.type, style: styles.lightboxImg })] }) }))] }));
};
const InfoField = ({ label, value }) => (_jsxs("div", { style: styles.fieldGroup, children: [_jsx("label", { style: styles.fieldLabel, children: label }), _jsx("p", { style: styles.fieldValue, children: value || '—' })] }));
const styles = {
    loadingCenter: {
        display: 'flex',
        justifyContent: 'center',
        padding: '80px 0',
    },
    spinner: {
        width: '32px',
        height: '32px',
        border: '3px solid rgba(0,0,0,0.1)',
        borderTopColor: '#0071e3',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    spinnerSmall: {
        width: '18px',
        height: '18px',
        border: '2px solid rgba(0,0,0,0.1)',
        borderTopColor: '#0071e3',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    backLink: {
        background: 'none',
        border: 'none',
        color: '#0071e3',
        fontSize: '15px',
        fontWeight: '500',
        cursor: 'pointer',
        padding: '0 0 20px 0',
        display: 'block',
    },
    card: {
        padding: '28px 32px',
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        marginBottom: '20px',
    },
    cardHeaderRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px',
    },
    projectTitle: {
        fontSize: '32px',
        fontWeight: '700',
        margin: 0,
        color: '#1d1d1f',
        letterSpacing: '-0.5px',
    },
    sectionTitle: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#1d1d1f',
        margin: '0 0 20px 0',
        letterSpacing: '-0.3px',
    },
    fieldGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px 24px',
    },
    fieldGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
    },
    fieldLabel: {
        fontSize: '12px',
        fontWeight: '600',
        color: '#86868b',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    fieldValue: {
        margin: 0,
        fontSize: '15px',
        color: '#1d1d1f',
        fontWeight: '400',
        lineHeight: '1.5',
    },
    input: {
        flex: 1,
        padding: '9px 12px',
        fontSize: '15px',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        color: '#1d1d1f',
        outline: 'none',
        minWidth: 0,
        width: '100%',
        boxSizing: 'border-box',
    },
    textarea: {
        width: '100%',
        padding: '10px 12px',
        fontSize: '15px',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        color: '#1d1d1f',
        outline: 'none',
        resize: 'vertical',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
    },
    checkInRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        backgroundColor: 'rgba(0,0,0,0.025)',
        borderRadius: '10px',
        marginBottom: '8px',
        flexWrap: 'wrap',
        gap: '8px',
    },
    photoColumn: {
        flex: '1 1 240px',
        minWidth: '200px',
    },
    photoColumnLabel: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#86868b',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        margin: '0 0 12px 0',
    },
    photoGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
        gap: '10px',
        marginBottom: '12px',
    },
    photoWrapper: {
        position: 'relative',
    },
    photoImg: {
        width: '100%',
        aspectRatio: '1',
        borderRadius: '10px',
        objectFit: 'cover',
        display: 'block',
        cursor: 'pointer',
        transition: 'opacity 0.15s ease',
    },
    photoDeleteBtn: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        backgroundColor: 'rgba(255,59,48,0.85)',
        color: '#fff',
        border: 'none',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '700',
        lineHeight: '1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    uploadButton: {
        padding: '10px 18px',
        backgroundColor: 'rgba(0,113,227,0.08)',
        color: '#0071e3',
        border: '1px solid rgba(0,113,227,0.3)',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        marginTop: '4px',
    },
    primaryButton: {
        padding: '10px 22px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    secondaryButton: {
        padding: '10px 22px',
        backgroundColor: 'rgba(0,0,0,0.06)',
        color: '#1d1d1f',
        border: 'none',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    ghostButton: {
        padding: '10px 22px',
        backgroundColor: 'transparent',
        color: '#86868b',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer',
    },
    dangerButton: {
        padding: '9px 18px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        color: '#c0392b',
        border: 'none',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    successButton: {
        alignSelf: 'flex-start',
        padding: '10px 22px',
        backgroundColor: 'rgba(52,199,89,0.12)',
        color: '#248a3d',
        border: 'none',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    errorBanner: {
        padding: '12px 16px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '10px',
        color: '#c0392b',
        fontSize: '14px',
    },
    emptySubText: {
        color: '#86868b',
        fontSize: '14px',
        margin: 0,
    },
    badgeGray: {
        display: 'inline-block',
        padding: '4px 12px',
        backgroundColor: 'rgba(142,142,147,0.15)',
        color: '#86868b',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
    },
    badgeBlue: {
        display: 'inline-block',
        padding: '4px 12px',
        backgroundColor: 'rgba(0,122,255,0.12)',
        color: '#0071e3',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
    },
    badgeOrange: {
        display: 'inline-block',
        padding: '4px 12px',
        backgroundColor: 'rgba(255,149,0,0.15)',
        color: '#c45c00',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
    },
    badgeGreen: {
        display: 'inline-block',
        padding: '4px 12px',
        backgroundColor: 'rgba(52,199,89,0.12)',
        color: '#248a3d',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
    },
    lightboxOverlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.82)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
    },
    lightboxContent: {
        position: 'relative',
        maxWidth: '900px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    lightboxClose: {
        position: 'absolute',
        top: '-48px',
        right: 0,
        background: 'none',
        border: 'none',
        color: '#fff',
        fontSize: '36px',
        lineHeight: '1',
        cursor: 'pointer',
        padding: '4px 8px',
    },
    lightboxLabel: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: '13px',
        fontWeight: '500',
        textAlign: 'center',
    },
    lightboxImg: {
        width: '100%',
        maxHeight: '80vh',
        objectFit: 'contain',
        borderRadius: '14px',
        display: 'block',
    },
};
//# sourceMappingURL=ProjectDetail.js.map